import { jsx as _jsx } from "react/jsx-runtime";
import { useSession } from 'components/Authenticated';
import { txt } from 'libs/i18n';
import ProjectAlert from '../../ProjectAlert';
const reportNotEnoughCredit = (login) => {
    // type of user should be defined during refactoring
    let paragraph = txt('contactTeamOwner');
    if (login.hasClaim('team:owner')) {
        if (login.hasClaim('orders:create')) {
            paragraph = txt('notEnoughCreditError');
        }
        else if (login.hasClaim('signIn:apa')) {
            paragraph = 'Sie haben nicht genug Guthaben. Wenden Sie sich bitte an APA-Salesdesk';
        }
        else {
            paragraph = txt('contactDistributor');
        }
    }
    return {
        title: txt('notEnoughCreditTitle'),
        paragraphs: [
            paragraph,
        ],
        contactSupport: false,
        errorType: 'error',
        showRetry: false,
        showReupload: true,
    };
};
const reportNotEnoughStorage = (login) => {
    let paragraph = txt('contactTeamOwner');
    if (login.hasClaim('team:owner')) {
        if (login.hasClaim('orders:create')) {
            paragraph = txt('notEnoughStorageLong');
        }
        else if (login.hasClaim('signIn:apa')) {
            paragraph = 'Sie haben nicht genügend Speicherplatz. Wenden Sie sich bitte an APA-Salesdesk:';
        }
        else {
            paragraph = txt('contactDistributor');
        }
    }
    return {
        title: txt('notEnoughStorage'),
        paragraphs: [
            paragraph,
        ],
        contactSupport: false,
        errorType: 'error',
        showRetry: false,
        showReupload: true,
    };
};
const reportProjectError = (projectError, canRetryAi) => {
    const { session } = useSession();
    if (projectError.category === 'Upload') {
        if (projectError.reason === 'TeamStorageQuotaExceeded') {
            return reportNotEnoughStorage(session.login);
        }
        return {
            title: txt('uploadErrorTitle'),
            paragraphs: [
                session.login.hasClaim('projects:uiUploadAfterFail') ? txt('uploadError') : txt('uploadErrorNewProject'),
            ],
            contactSupport: true,
            errorType: 'error',
            showRetry: false,
            showReupload: true,
        };
    }
    if (projectError.category === 'Transcoding') {
        return {
            title: txt('transcodingErrorTitle'),
            paragraphs: [
                txt('transcodingError'),
            ],
            contactSupport: true,
            errorType: 'error',
            showRetry: true,
            showReupload: true,
        };
    }
    if (projectError.category === 'Transcription') {
        // NOTE: AI actions (e.g. Smart translation) run through the same pipeline as
        // the transcription, so their failure also arrives as a 'Transcription' error.
        // When the transcription itself is intact, offer an AI retry instead of
        // re-upload/re-transcription.
        if (canRetryAi) {
            return {
                title: txt('aiActionErrorTitle'),
                paragraphs: [
                    txt('aiActionError'),
                ],
                contactSupport: false,
                errorType: 'warning',
                showRetry: false,
                showReupload: false,
            };
        }
        const paragraphs = session.login.hasClaim('projects:retryTranscription')
            ? [txt('transcriptionError')]
            : [txt('transcriptionError'), txt('tryDifferentTranscriber')];
        return {
            title: txt('transcriptionErrorTitle'),
            paragraphs,
            contactSupport: true,
            errorType: 'error',
            showRetry: true,
            showReupload: true,
        };
    }
    if (projectError.category === 'Client') {
        if (projectError.reason === 'TrsxLoadFail') {
            return {
                title: txt('trsxLoadErrorTitle'),
                paragraphs: [
                    txt('trsxLoadError'),
                ],
                contactSupport: true,
                errorType: 'error',
                showRetry: false,
                showReupload: true,
            };
        }
        if (projectError.reason === 'FailedTranscriptionAccessDenied') {
            return {
                title: canRetryAi ? txt('failedAiActionNoAccess') : txt('failedTranscriptionNoAccess'),
                paragraphs: [],
                contactSupport: false,
                errorType: 'warning',
                compact: true,
                showRetry: false,
                showReupload: !canRetryAi,
            };
        }
        if (projectError.reason === 'FailedTranscriptionPartialLoaded') {
            return {
                title: canRetryAi ? txt('failedAiActionPartial') : txt('failedTranscriptionPartial'),
                paragraphs: [],
                contactSupport: false,
                errorType: 'warning',
                compact: true,
                showRetry: true,
                showReupload: !canRetryAi,
            };
        }
        return {
            title: txt('missingRecording'),
            paragraphs: [
                txt('missingRecordingBody'),
            ],
            contactSupport: true,
            errorType: 'warning',
            showRetry: false,
            showReupload: true,
        };
    }
    if (projectError.reason === 'NotEnoughCredit') {
        return reportNotEnoughCredit(session.login);
    }
    if (projectError.reason === 'ServerShutdown') {
        return {
            title: txt('serverShutdownTitle'),
            paragraphs: [
                txt('serverShutdown'),
            ],
            contactSupport: true,
            errorType: 'error',
            showRetry: true,
            showReupload: true,
        };
    }
    if (projectError.reason === 'Canceled') {
        return {
            title: txt('canceledProject'),
            paragraphs: [
                txt('canceledProjectError'),
            ],
            contactSupport: true,
            errorType: 'error',
            showRetry: true,
            showReupload: true,
        };
    }
    return {
        title: txt('unknownProjectErrorTitle'),
        paragraphs: [
            txt('unknownProjectError'),
        ],
        contactSupport: true,
        errorType: 'error',
        showRetry: true,
        showReupload: true,
    };
};
const ErrorAlert = ({ projectError, onClick, onRetry, onRetryAi, alternativeProfileName, retryLoading, }) => {
    const canRetryAi = onRetryAi !== undefined
        && (projectError.category === 'Transcription'
            || (projectError.category === 'Client'
                && (projectError.reason === 'FailedTranscriptionAccessDenied'
                    || projectError.reason === 'FailedTranscriptionPartialLoaded')));
    const errorReport = reportProjectError(projectError, canRetryAi);
    const description = (errorReport.paragraphs.map((parText, index) => (
    // eslint-disable-next-line react/no-array-index-key
    parText === null ? null : _jsx("p", { children: parText }, index))));
    return (_jsx(ProjectAlert, { compact: errorReport.compact, type: errorReport.errorType, title: errorReport.title, description: description, contactSupport: errorReport.contactSupport, onClick: errorReport.showReupload ? onClick : undefined, onRetry: errorReport.showRetry ? onRetry : undefined, onRetryAi: canRetryAi ? onRetryAi : undefined, alternativeProfileName: errorReport.showRetry ? alternativeProfileName : undefined, retryLoading: retryLoading }));
};
export default ErrorAlert;
